// Tiny i18n runtime shared by the API and the web client.
//
// `en.ts` defines the key set; every other locale is typed against it, so the
// compiler catches a missing translation. There is no runtime dependency and no
// async loading: both locales are small enough to ship together.

import { en, type TranslationKey, type Translations } from './en';
import { es } from './es';
import type { Locale } from '../types';

export type { TranslationKey, Translations };

export const translations: Record<Locale, Translations> = { en, es };

/** Values substituted into {placeholder} slots. */
export type TranslationVars = Record<string, string | number>;

export function translate(locale: Locale, key: TranslationKey, vars?: TranslationVars): string {
  const template = translations[locale]?.[key] ?? en[key] ?? key;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match);
}

/** Bound translator, so callers write `t('key')` instead of passing the locale. */
export function translator(locale: Locale) {
  return (key: TranslationKey, vars?: TranslationVars) => translate(locale, key, vars);
}

export type Translator = ReturnType<typeof translator>;

const LOCALE_TAGS: Record<Locale, string> = { es: 'es-ES', en: 'en-GB' };

/** BCP 47 tag for Intl APIs. */
export function localeTag(locale: Locale): string {
  return LOCALE_TAGS[locale] ?? 'en-GB';
}

/** Picks the best supported locale from an Accept-Language header. */
export function negotiateLocale(header: string | undefined, fallback: Locale): Locale {
  if (!header) return fallback;
  const ranked = header
    .split(',')
    .map((part) => {
      const [tag, ...params] = part.trim().split(';');
      const q = params.find((p) => p.trim().startsWith('q='));
      return { tag: (tag ?? '').toLowerCase(), q: q ? Number.parseFloat(q.split('=')[1] ?? '0') : 1 };
    })
    .filter((entry) => entry.tag.length > 0)
    .sort((a, b) => b.q - a.q);

  for (const { tag } of ranked) {
    const base = tag.split('-')[0];
    if (base === 'es' || base === 'en') return base;
  }
  return fallback;
}
